import * as React from "react";
import {inject, observer} from "mobx-react";
import type {IModel, ISpaceItemModel} from "../../../data/models/Model";
import {KeyboardListener} from "../../../utils/interaction/key/KeyboardListener";
import type {Catalog} from "../../../data/models/Catalog";
import {CatalogIconType, XyiconFeature} from "../../../generated/api/base";
import type {IIconConfig} from "../../modules/catalog/create/CatalogTypes";
import type {TransportLayer} from "../../../data/TransportLayer";
import type {AppState} from "../../../data/state/AppState";
import {TOPBAR_HEIGHT, zIndex} from "../styles/styles";
import {Panel} from "../abstract/Panel";
import {CatalogItemPanelV5} from "../abstract/view/catalog/CatalogItemPanelV5";
import {LoaderV5} from "../loader/LoaderV5";
import {DetailsContentV5} from "./DetailsContentV5";
import {ShadowDivStyled} from "./ShadowDiv";

const CatalogItemPanelShadowDivStyle: React.CSSProperties = {
	zIndex: zIndex.contextOptions,
};

const CatalogItemPanelStyle: React.CSSProperties = {
	zIndex: zIndex.contextOptions + 1,
};

interface IDetailsContainerProps {
	readonly items: IModel[];
	readonly transport?: TransportLayer;
	readonly appState?: AppState;
	readonly isScopeWideSearchOpen?: boolean;
	readonly onClose: (closeAll?: boolean) => void;
	readonly closeWideSearchPanel: () => void;
}

interface IDetailsContainerState {
	isCatalogEditPanelOpen: boolean;
	iconConfig: IIconConfig;
	editedCatalog: Catalog;
	isPortTemplateEditorOpen: boolean;
	isLoadingCatalogEditor: boolean;
}

@inject("appState")
@inject("transport")
@observer
export class DetailsContainerV5 extends React.Component<IDetailsContainerProps, IDetailsContainerState> {
	// They're on top of each other, so we don't need to render more than 2, even if we introduce an opening/closing animation in the future
	private readonly _numberOfItemsToRender: number = 1;

	constructor(props: IDetailsContainerProps) {
		super(props);

		this.state = {
			isCatalogEditPanelOpen: false,
			iconConfig: null,
			editedCatalog: null,
			isPortTemplateEditorOpen: false,
			isLoadingCatalogEditor: false,
		};
	}

	private readonly onCloseClick = () => {
		if (!this.state.isCatalogEditPanelOpen) {
			this.props.onClose(false);
		}
	};

	private readonly onKeyUp = (event: KeyboardEvent) => {
		if (event.key === KeyboardListener.KEY_ESCAPE && !this.state.isCatalogEditPanelOpen) {
			this.onCloseClick();
		}
	};

	private readonly onCatalogIconClick = async () => {
		const {transport, items} = this.props;

		if (!this.state.isLoadingCatalogEditor) {
			this.setState({
				isLoadingCatalogEditor: true,
			});

			const catalogs = items.filter((item) => item.ownFeature === XyiconFeature.XyiconCatalog);
			const catalog = catalogs[0] as Catalog;

			const iconConfig = await transport.getIconConfigOfCatalog(catalog);

			this.setState({
				editedCatalog: catalog,
				iconConfig: iconConfig,
				isCatalogEditPanelOpen: true,
				isLoadingCatalogEditor: false,
			});
		}
	};

	private readonly closeCreatePanel = () => {
		this.setState({
			isCatalogEditPanelOpen: false,
			editedCatalog: null,
			iconConfig: null,
		});
	};

	private readonly onPortTemplateEditorChange = (value: boolean) => {
		this.setState({isPortTemplateEditorOpen: value});
	};

	public override componentDidMount() {
		KeyboardListener.getInstance().signals.up.add(this.onKeyUp);
	}

	public override componentDidUpdate(prevProps: Readonly<IDetailsContainerProps>, prevState: Readonly<{}>, snapshot?: any): void {
		if (prevProps.items.length !== this.props.items.length) {
			this.props.appState.isDetailsContainerOpened = !!this.props.items.length;
			if (this.props.items.length === 0) {
				this.setState({isCatalogEditPanelOpen: false});
			}
		}
	}

	public override componentWillUnmount() {
		KeyboardListener.getInstance().signals.up.remove(this.onKeyUp);
	}

	public override render() {
		const {items, closeWideSearchPanel, isScopeWideSearchOpen} = this.props;
		const isDetailsPanelOverlayed = this.props.appState.app.overlayedDetailedItems.length > 0;
		const {isCatalogEditPanelOpen, editedCatalog, iconConfig, isPortTemplateEditorOpen} = this.state;
		const itemsToRender = items.filter((item: IModel, index: number) => item && index >= items.length - this._numberOfItemsToRender);
		const style: React.CSSProperties = {zIndex: zIndex.overlayDetailsPanel, height: "100%"};

		if (isScopeWideSearchOpen || !isDetailsPanelOverlayed) {
			style.height = `calc(100% - ${TOPBAR_HEIGHT}px)`;
			style.top = TOPBAR_HEIGHT;
		}

		return (
			<>
				{items.length > 0 && !isScopeWideSearchOpen && isDetailsPanelOverlayed && (
					<ShadowDivStyled
						className="shadowDiv"
						onClick={this.onCloseClick}
					/>
				)}
				<Panel
					isOpen={items.length > 0}
					style={style}
					panelId="details-container-panel"
				>
					{itemsToRender.map((spaceItemModel: ISpaceItemModel, index: number) => {
						const detailsContentProps = {
							items: [spaceItemModel],
							feature: spaceItemModel.ownFeature,
							features: [spaceItemModel.ownFeature],
							isPortTemplateEditorOpen: isPortTemplateEditorOpen,
							insideDetailsContainer: true,
							setPortTemplateEditorOpen: this.onPortTemplateEditorChange,
							closeWideSearchPanel: closeWideSearchPanel,
							onCatalogIconEditClick: this.onCatalogIconClick,
						};

						return (
							<React.Fragment key={spaceItemModel.id}>
								<DetailsContentV5
									{...detailsContentProps}
									onCloseOverlayedDetaislPanel={this.onCloseClick}
								/>
							</React.Fragment>
						);
					})}
				</Panel>
				{this.state.isLoadingCatalogEditor && (
					<ShadowDivStyled
						className="shadowDiv"
						style={CatalogItemPanelShadowDivStyle}
					>
						<LoaderV5 />
					</ShadowDivStyled>
				)}
				{isCatalogEditPanelOpen && editedCatalog && iconConfig && (
					<>
						<ShadowDivStyled
							className="shadowDiv"
							style={CatalogItemPanelShadowDivStyle}
						/>
						<CatalogItemPanelV5
							onClose={this.closeCreatePanel}
							catalog={this.state.editedCatalog}
							iconConfig={this.state.iconConfig}
							mode="edit"
							shape={this.state.editedCatalog.iconCategory === CatalogIconType.ModelParameter ? "Custom Shape" : "Default Shape"}
							step={this.state.editedCatalog.iconCategory === CatalogIconType.ModelParameter ? "Item properties" : "Select model"}
							style={CatalogItemPanelStyle}
						/>
					</>
				)}
			</>
		);
	}
}
